import api from './api';

export interface Profile {
  id: string;
  description: string;
  code: string;
}

export interface Bank {
  id: string;
  name: string;
  code: string;
  active: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  active: string;
}

export interface ServiceType {
  id: string;
  name: string;
  categoryId: string;
  active: string;
}

export interface PosSisp {
  id: string;
  designation: string;
  pos: string;
  posAuthCode: string;
  posUrl: string;
  /** Código do serviço que este terminal serve. null = catch-all (todos os serviços) */
  servico?: string | null;
  /** Dados bancários da conta de liquidação deste POS (informativos) */
  nib?: string | null;
  iban?: string | null;
  swift?: string | null;
  banco?: string | null;
  environment: string;
  status: string;
  createdAt?: string;
}

/** Ambiente activo de um serviço — devolvido por GET /settings/sisp-ambiente/servicos */
export interface SispAmbienteServico {
  servico: string | null;
  ambiente: 'T' | 'P' | null;
  total: number;
}

export interface CardType {
  id: string;
  codigo: string;
  nome: string;
  descricao?: string;
  icone?: string;
  cor?: string;
  ordem?: number;
  estado?: string;
}

export interface SispTokenization {
  id: string;
  pos: string;
  posAuthCode: string;
  requestUrl: string;
  cancelUrl: string;
  paymentUrl: string;
  environment: 'sandbox' | 'production';
  createdAt?: string;
}

export interface EntityGroup {
  id: string;
  codigo: string;
  nome: string;
  descricao?: string;
  estado: string;
  membrosCount?: number;
  dataRegisto?: string;
}

export interface Language {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  active: string;
}

export interface SmsInterval {
  id: string;
  code: string;
  description: string;
  minutes: number;
  active: string;
}

export interface SispEntity {
  id: string;
  entityId?: string;
  entityName?: string;
  sispEntity: string;
  merchant?: string;
  subMerchant?: string;
  pos?: string;
  active: string;
}

export interface SispService {
  id: string;
  serviceId?: string;
  serviceName?: string;
  sispEntityId?: string;
  sispEntity?: string;
  sispService: string;
  active: string;
}

export interface TwilioConfig {
  id: string;
  description: string;
  accountSid: string;
  authToken: string;
  phoneNumber?: string;
  messagingServiceSid?: string;
  voiceWebhookUrl?: string;
  environment: 'sandbox' | 'production';
  active: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TwilioConfigInput {
  description: string;
  accountSid: string;
  authToken: string;
  phoneNumber?: string;
  messagingServiceSid?: string;
  voiceWebhookUrl?: string;
  environment?: 'sandbox' | 'production';
  active?: boolean;
}

// SMS Templates
export interface SmsTemplate {
  id: string;
  code: string;
  name: string;
  content: string;
  variables?: string[];
  type: 'transacional' | 'marketing' | 'notificacao' | 'otp';
  active: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SmsTemplateInput {
  code: string;
  name: string;
  content: string;
  variables?: string[];
  type?: 'transacional' | 'marketing' | 'notificacao' | 'otp';
  active?: boolean;
}

// SMS Logs
export interface SmsLog {
  id: string;
  twilioConfigId?: string;
  twilioDescription?: string;
  templateId?: string;
  templateName?: string;
  phoneNumber: string;
  content: string;
  variablesUsed?: Record<string, string>;
  twilioSid?: string;
  status: 'pendente' | 'enviado' | 'entregue' | 'falhou' | 'rejeitado';
  errorMessage?: string;
  cost?: number;
  segments?: number;
  direction: 'outbound' | 'inbound';
  createdAt?: string;
}

export interface SmsLogsParams {
  page?: number;
  limit?: number;
  status?: string;
  templateId?: string;
  phoneNumber?: string;
  startDate?: string;
  endDate?: string;
}

export interface SmsLogsResponse {
  success: boolean;
  data: SmsLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// SMS Stats
export interface SmsStats {
  total: number;
  enviado: number;
  entregue: number;
  falhou: number;
  pendente: number;
  custoTotal: number;
  segmentosTotal: number;
  hoje: {
    total: number;
    enviado: number;
    falhou: number;
  };
  ultimosSete: {
    total: number;
    enviado: number;
    falhou: number;
  };
}

// Send SMS Input
export interface SendSmsInput {
  to: string;
  templateCode?: string;
  message?: string;
  variables?: Record<string, string>;
  twilioConfigId?: string;
}

export interface SendSmsResponse {
  success: boolean;
  data: {
    logId: string;
    twilioSid: string;
    status: string;
    to: string;
    from: string;
    segments: number;
  };
}

// ============================================
// EMAIL Types
// ============================================
export interface EmailConfig {
  id: string;
  description: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
  fromName: string;
  fromEmail: string;
  replyTo?: string;
  environment: 'sandbox' | 'producao';
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EmailConfigInput {
  description: string;
  smtpHost: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUser: string;
  smtpPassword: string;
  fromName?: string;
  fromEmail: string;
  replyTo?: string;
  environment?: 'sandbox' | 'producao';
  active?: boolean;
}

export interface EmailSignature {
  id: string;
  name: string;
  contentHtml: string;
  active: boolean;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EmailSignatureInput {
  name: string;
  contentHtml: string;
  active?: boolean;
  isDefault?: boolean;
}

export interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  subject: string;
  contentHtml: string;
  contentText?: string;
  variables?: string[];
  type: 'transacional' | 'marketing' | 'notificacao' | 'sistema';
  signatureId?: string;
  signatureName?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EmailTemplateInput {
  code: string;
  name: string;
  subject: string;
  contentHtml: string;
  contentText?: string;
  variables?: string[];
  type?: 'transacional' | 'marketing' | 'notificacao' | 'sistema';
  signatureId?: string;
  active?: boolean;
}

export interface EmailLog {
  id: string;
  emailConfigId?: string;
  emailConfig?: string;
  templateId?: string;
  templateName?: string;
  email: string;
  subject: string;
  contentHtml?: string;
  contentText?: string;
  variablesUsed?: Record<string, string>;
  messageId?: string;
  status: 'pendente' | 'enviado' | 'entregue' | 'falhou' | 'rejeitado' | 'aberto' | 'clicado';
  errorMessage?: string;
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
}

export interface EmailLogsParams {
  page?: number;
  limit?: number;
  status?: string;
  email?: string;
  startDate?: string;
  endDate?: string;
}

export interface EmailLogsResponse {
  success: boolean;
  data: EmailLog[];
  stats: {
    total: number;
    sent: number;
    delivered: number;
    failed: number;
    pending: number;
    opened: number;
    clicked: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface EmailStats {
  today: { total: number; sent: number; delivered: number; failed: number; opened: number; clicked: number };
  week: { total: number; sent: number; delivered: number; failed: number; opened: number; clicked: number };
  month: { total: number; sent: number; delivered: number; failed: number; opened: number; clicked: number };
  all: { total: number; sent: number; delivered: number; failed: number; opened: number; clicked: number };
}

export interface SendEmailInput {
  email: string;
  subject?: string;
  contentHtml?: string;
  contentText?: string;
  templateId?: string;
  variables?: Record<string, string>;
  emailConfigId?: string;
  includeSignature?: boolean;
}

export interface SendEmailTemplateInput {
  email: string;
  templateCode: string;
  variables?: Record<string, string>;
}

export interface SendEmailResponse {
  success: boolean;
  data: {
    logId: string;
    messageId: string;
    status: string;
  };
}

export interface SettingsResponse<T> {
  success: boolean;
  data: T[];
}

class SettingsService {
  /**
   * Get all user profiles
   */
  async getProfiles(): Promise<SettingsResponse<Profile>> {
    try {
      const response = await api.get('/admin/settings/profiles');
      return response.data;
    } catch (error) {
      console.error('Get profiles error:', error);
      throw error;
    }
  }

  /**
   * Create new profile
   */
  async createProfile(data: Partial<Profile>): Promise<Profile> {
    try {
      const response = await api.post('/admin/settings/profiles', data);
      return response.data.data;
    } catch (error) {
      console.error('Create profile error:', error);
      throw error;
    }
  }

  /**
   * Update profile
   */
  async updateProfile(id: string, data: Partial<Profile>): Promise<void> {
    try {
      await api.put(`/admin/settings/profiles/${id}`, data);
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  /**
   * Delete profile
   */
  async deleteProfile(id: string): Promise<void> {
    try {
      await api.delete(`/admin/settings/profiles/${id}`);
    } catch (error) {
      console.error('Delete profile error:', error);
      throw error;
    }
  }

  /**
   * Get all banks
   */
  async getBanks(): Promise<SettingsResponse<Bank>> {
    try {
      const response = await api.get('/admin/settings/banks');
      return response.data;
    } catch (error) {
      console.error('Get banks error:', error);
      throw error;
    }
  }

  /**
   * Get all service categories
   */
  async getServiceCategories(): Promise<SettingsResponse<ServiceCategory>> {
    try {
      const response = await api.get('/admin/settings/service-categories');
      return response.data;
    } catch (error) {
      console.error('Get service categories error:', error);
      throw error;
    }
  }

  /**
   * Get all service types
   */
  async getServiceTypes(categoryId?: string): Promise<SettingsResponse<ServiceType>> {
    try {
      const params = categoryId ? `?categoryId=${categoryId}` : '';
      const response = await api.get(`/admin/settings/service-types${params}`);
      return response.data;
    } catch (error) {
      console.error('Get service types error:', error);
      throw error;
    }
  }

  /**
   * POS SISP Methods
   */
  async getPosSisp(): Promise<SettingsResponse<PosSisp>> {
    try {
      const response = await api.get('/admin/settings/pos-sisp');
      return response.data;
    } catch (error) {
      console.error('Get POS SISP error:', error);
      throw error;
    }
  }

  async createPosSisp(data: Partial<PosSisp>): Promise<PosSisp> {
    try {
      const response = await api.post('/admin/settings/pos-sisp', data);
      return response.data.data;
    } catch (error) {
      console.error('Create POS SISP error:', error);
      throw error;
    }
  }

  async updatePosSisp(id: string, data: Partial<PosSisp>): Promise<void> {
    try {
      await api.put(`/admin/settings/pos-sisp/${id}`, data);
    } catch (error) {
      console.error('Update POS SISP error:', error);
      throw error;
    }
  }

  async deletePosSisp(id: string): Promise<void> {
    try {
      await api.delete(`/admin/settings/pos-sisp/${id}`);
    } catch (error) {
      console.error('Delete POS SISP error:', error);
      throw error;
    }
  }

  // SISP Ambiente
  async getSispAmbiente(): Promise<{ success: boolean; data: { ambiente: 'T' | 'P' } }> {
    try {
      const response = await api.get('/admin/settings/sisp-ambiente');
      return response.data;
    } catch (error) {
      console.error('Get SISP ambiente error:', error);
      throw error;
    }
  }

  async setSispAmbiente(ambiente: 'T' | 'P'): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.put('/admin/settings/sisp-ambiente', { ambiente });
      return response.data;
    } catch (error: any) {
      console.error('Set SISP ambiente error:', error);
      throw error?.response?.data || error;
    }
  }

  /** Ambiente activo de cada serviço, numa só chamada */
  async getSispAmbientePorServico(): Promise<{ success: boolean; data: SispAmbienteServico[] }> {
    try {
      const response = await api.get('/admin/settings/sisp-ambiente/servicos');
      return response.data;
    } catch (error) {
      console.error('Get SISP ambiente por serviço error:', error);
      throw error;
    }
  }

  /** Troca o ambiente apenas de um serviço. servico null = catch-all */
  async setSispAmbienteServico(
    servico: string | null,
    ambiente: 'T' | 'P'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.put(`/admin/settings/sisp-ambiente/${servico ?? 'default'}`, { ambiente });
      return response.data;
    } catch (error: any) {
      console.error('Set SISP ambiente por serviço error:', error);
      throw error?.response?.data || error;
    }
  }

  /**
   * SISP Tokenization Methods
   */
  async getSispTokenization(): Promise<SettingsResponse<SispTokenization>> {
    try {
      const response = await api.get('/admin/settings/sisp-tokenization');
      return response.data;
    } catch (error) {
      console.error('Get SISP tokenization error:', error);
      throw error;
    }
  }

  async createSispTokenization(data: Partial<SispTokenization>): Promise<SispTokenization> {
    try {
      const response = await api.post('/admin/settings/sisp-tokenization', data);
      return response.data.data;
    } catch (error) {
      console.error('Create SISP tokenization error:', error);
      throw error;
    }
  }

  async updateSispTokenization(id: string, data: Partial<SispTokenization>): Promise<void> {
    try {
      await api.put(`/admin/settings/sisp-tokenization/${id}`, data);
    } catch (error) {
      console.error('Update SISP tokenization error:', error);
      throw error;
    }
  }

  async deleteSispTokenization(id: string): Promise<void> {
    try {
      await api.delete(`/admin/settings/sisp-tokenization/${id}`);
    } catch (error) {
      console.error('Delete SISP tokenization error:', error);
      throw error;
    }
  }

  /**
   * Entity Groups Methods
   */
  async getEntityGroups(params?: { page?: number; limit?: number; search?: string; status?: string }): Promise<SettingsResponse<EntityGroup> & { stats?: any; pagination?: any }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status) queryParams.append('status', params.status);

      const url = queryParams.toString() ? `/admin/entity-groups?${queryParams}` : '/admin/entity-groups';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Get entity groups error:', error);
      throw error;
    }
  }

  async getEntityGroup(id: string): Promise<{ success: boolean; data: EntityGroup & { membros?: any[] } }> {
    try {
      const response = await api.get(`/admin/entity-groups/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get entity group error:', error);
      throw error;
    }
  }

  async createEntityGroup(data: { codigo: string; nome: string; descricao?: string; estado?: string }): Promise<EntityGroup> {
    try {
      const response = await api.post('/admin/entity-groups', data);
      return response.data.data;
    } catch (error) {
      console.error('Create entity group error:', error);
      throw error;
    }
  }

  async updateEntityGroup(id: string, data: Partial<EntityGroup>): Promise<void> {
    try {
      await api.put(`/admin/entity-groups/${id}`, data);
    } catch (error) {
      console.error('Update entity group error:', error);
      throw error;
    }
  }

  async deleteEntityGroup(id: string): Promise<void> {
    try {
      await api.delete(`/admin/entity-groups/${id}`);
    } catch (error) {
      console.error('Delete entity group error:', error);
      throw error;
    }
  }

  async addEntityToGroup(groupId: string, entidadeId: string): Promise<void> {
    try {
      await api.post(`/admin/entity-groups/${groupId}/members`, { entidadeId });
    } catch (error) {
      console.error('Add entity to group error:', error);
      throw error;
    }
  }

  async removeEntityFromGroup(groupId: string, membroId: string): Promise<void> {
    try {
      await api.delete(`/admin/entity-groups/${groupId}/members/${membroId}`);
    } catch (error) {
      console.error('Remove entity from group error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Banks
  // ============================================
  async getParametrizacoesBanks(): Promise<SettingsResponse<Bank>> {
    try {
      const response = await api.get('/admin/parametrizacoes/bancos');
      return response.data;
    } catch (error) {
      console.error('Get banks error:', error);
      throw error;
    }
  }

  async createBank(data: Partial<Bank>): Promise<Bank> {
    try {
      const response = await api.post('/admin/parametrizacoes/bancos', data);
      return response.data.data;
    } catch (error) {
      console.error('Create bank error:', error);
      throw error;
    }
  }

  async updateBank(id: string, data: Partial<Bank>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/bancos/${id}`, data);
    } catch (error) {
      console.error('Update bank error:', error);
      throw error;
    }
  }

  async deleteBank(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/bancos/${id}`);
    } catch (error) {
      console.error('Delete bank error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Languages
  // ============================================
  async getLanguages(): Promise<SettingsResponse<Language>> {
    try {
      const response = await api.get('/admin/parametrizacoes/languages');
      return response.data;
    } catch (error) {
      console.error('Get languages error:', error);
      throw error;
    }
  }

  async createLanguage(data: Partial<Language>): Promise<Language> {
    try {
      const response = await api.post('/admin/parametrizacoes/languages', data);
      return response.data.data;
    } catch (error) {
      console.error('Create language error:', error);
      throw error;
    }
  }

  async updateLanguage(id: string, data: Partial<Language>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/languages/${id}`, data);
    } catch (error) {
      console.error('Update language error:', error);
      throw error;
    }
  }

  async deleteLanguage(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/languages/${id}`);
    } catch (error) {
      console.error('Delete language error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - SMS Intervals
  // ============================================
  async getSmsIntervals(): Promise<SettingsResponse<SmsInterval>> {
    try {
      const response = await api.get('/admin/parametrizacoes/sms-intervals');
      return response.data;
    } catch (error) {
      console.error('Get SMS intervals error:', error);
      throw error;
    }
  }

  async createSmsInterval(data: Partial<SmsInterval>): Promise<SmsInterval> {
    try {
      const response = await api.post('/admin/parametrizacoes/sms-intervals', data);
      return response.data.data;
    } catch (error) {
      console.error('Create SMS interval error:', error);
      throw error;
    }
  }

  async updateSmsInterval(id: string, data: Partial<SmsInterval>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/sms-intervals/${id}`, data);
    } catch (error) {
      console.error('Update SMS interval error:', error);
      throw error;
    }
  }

  async deleteSmsInterval(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/sms-intervals/${id}`);
    } catch (error) {
      console.error('Delete SMS interval error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - SISP Entities
  // ============================================
  async getSispEntities(): Promise<SettingsResponse<SispEntity>> {
    try {
      const response = await api.get('/admin/parametrizacoes/sisp-entities');
      return response.data;
    } catch (error) {
      console.error('Get SISP entities error:', error);
      throw error;
    }
  }

  async createSispEntity(data: Partial<SispEntity>): Promise<SispEntity> {
    try {
      const response = await api.post('/admin/parametrizacoes/sisp-entities', data);
      return response.data.data;
    } catch (error) {
      console.error('Create SISP entity error:', error);
      throw error;
    }
  }

  async updateSispEntity(id: string, data: Partial<SispEntity>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/sisp-entities/${id}`, data);
    } catch (error) {
      console.error('Update SISP entity error:', error);
      throw error;
    }
  }

  async deleteSispEntity(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/sisp-entities/${id}`);
    } catch (error) {
      console.error('Delete SISP entity error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - SISP Services
  // ============================================
  async getSispServices(): Promise<SettingsResponse<SispService>> {
    try {
      const response = await api.get('/admin/parametrizacoes/sisp-services');
      return response.data;
    } catch (error) {
      console.error('Get SISP services error:', error);
      throw error;
    }
  }

  async createSispService(data: Partial<SispService>): Promise<SispService> {
    try {
      const response = await api.post('/admin/parametrizacoes/sisp-services', data);
      return response.data.data;
    } catch (error) {
      console.error('Create SISP service error:', error);
      throw error;
    }
  }

  async updateSispService(id: string, data: Partial<SispService>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/sisp-services/${id}`, data);
    } catch (error) {
      console.error('Update SISP service error:', error);
      throw error;
    }
  }

  async deleteSispService(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/sisp-services/${id}`);
    } catch (error) {
      console.error('Delete SISP service error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Migrate
  // ============================================
  async migrateParametrizacoes(): Promise<{ success: boolean; created: string[] }> {
    try {
      const response = await api.get('/admin/parametrizacoes/migrate');
      return response.data;
    } catch (error) {
      console.error('Migrate parametrizacoes error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Twilio Config
  // ============================================
  async getTwilioConfigs(): Promise<SettingsResponse<TwilioConfig>> {
    try {
      const response = await api.get('/admin/parametrizacoes/twilio');
      return response.data;
    } catch (error) {
      console.error('Get Twilio configs error:', error);
      throw error;
    }
  }

  async getTwilioConfig(id: string): Promise<{ success: boolean; data: TwilioConfig }> {
    try {
      const response = await api.get(`/admin/parametrizacoes/twilio/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get Twilio config error:', error);
      throw error;
    }
  }

  async createTwilioConfig(data: TwilioConfigInput): Promise<TwilioConfig> {
    try {
      const response = await api.post('/admin/parametrizacoes/twilio', data);
      return response.data.data;
    } catch (error) {
      console.error('Create Twilio config error:', error);
      throw error;
    }
  }

  async updateTwilioConfig(id: string, data: Partial<TwilioConfigInput>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/twilio/${id}`, data);
    } catch (error) {
      console.error('Update Twilio config error:', error);
      throw error;
    }
  }

  async deleteTwilioConfig(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/twilio/${id}`);
    } catch (error) {
      console.error('Delete Twilio config error:', error);
      throw error;
    }
  }

  async getActiveTwilioConfig(): Promise<{ success: boolean; data: TwilioConfig }> {
    try {
      const response = await api.get('/admin/parametrizacoes/twilio-active');
      return response.data;
    } catch (error) {
      console.error('Get active Twilio config error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - SMS Templates
  // ============================================
  async getSmsTemplates(): Promise<SettingsResponse<SmsTemplate>> {
    try {
      const response = await api.get('/admin/parametrizacoes/sms-templates');
      return response.data;
    } catch (error) {
      console.error('Get SMS templates error:', error);
      throw error;
    }
  }

  async getSmsTemplate(id: string): Promise<{ success: boolean; data: SmsTemplate }> {
    try {
      const response = await api.get(`/admin/parametrizacoes/sms-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get SMS template error:', error);
      throw error;
    }
  }

  async createSmsTemplate(data: SmsTemplateInput): Promise<SmsTemplate> {
    try {
      const response = await api.post('/admin/parametrizacoes/sms-templates', data);
      return response.data.data;
    } catch (error) {
      console.error('Create SMS template error:', error);
      throw error;
    }
  }

  async updateSmsTemplate(id: string, data: Partial<SmsTemplateInput>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/sms-templates/${id}`, data);
    } catch (error) {
      console.error('Update SMS template error:', error);
      throw error;
    }
  }

  async deleteSmsTemplate(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/sms-templates/${id}`);
    } catch (error) {
      console.error('Delete SMS template error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - SMS Logs
  // ============================================
  async getSmsLogs(params?: SmsLogsParams): Promise<SmsLogsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.templateId) queryParams.append('templateId', params.templateId);
      if (params?.phoneNumber) queryParams.append('phoneNumber', params.phoneNumber);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const url = queryParams.toString()
        ? `/admin/parametrizacoes/sms-logs?${queryParams}`
        : '/admin/parametrizacoes/sms-logs';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Get SMS logs error:', error);
      throw error;
    }
  }

  async getSmsLog(id: string): Promise<{ success: boolean; data: SmsLog }> {
    try {
      const response = await api.get(`/admin/parametrizacoes/sms-logs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get SMS log error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - SMS Stats
  // ============================================
  async getSmsStats(): Promise<{ success: boolean; data: SmsStats }> {
    try {
      const response = await api.get('/admin/parametrizacoes/sms-stats');
      return response.data;
    } catch (error) {
      console.error('Get SMS stats error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Send SMS
  // ============================================
  async sendSms(data: SendSmsInput): Promise<SendSmsResponse> {
    try {
      // Map frontend field names to backend expected names
      const payload = {
        phone: data.to,
        content: data.message,
        templateId: data.templateCode,
        variables: data.variables,
        twilioConfigId: data.twilioConfigId,
      };
      const response = await api.post('/admin/parametrizacoes/sms-send', payload);
      return response.data;
    } catch (error) {
      console.error('Send SMS error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Email Config
  // ============================================
  async getEmailConfigs(): Promise<SettingsResponse<EmailConfig>> {
    try {
      const response = await api.get('/admin/parametrizacoes/email-config');
      return response.data;
    } catch (error) {
      console.error('Get email configs error:', error);
      throw error;
    }
  }

  async getEmailConfig(id: string): Promise<{ success: boolean; data: EmailConfig }> {
    try {
      const response = await api.get(`/admin/parametrizacoes/email-config/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get email config error:', error);
      throw error;
    }
  }

  async createEmailConfig(data: EmailConfigInput): Promise<EmailConfig> {
    try {
      const response = await api.post('/admin/parametrizacoes/email-config', data);
      return response.data.data;
    } catch (error) {
      console.error('Create email config error:', error);
      throw error;
    }
  }

  async updateEmailConfig(id: string, data: Partial<EmailConfigInput>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/email-config/${id}`, data);
    } catch (error) {
      console.error('Update email config error:', error);
      throw error;
    }
  }

  async deleteEmailConfig(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/email-config/${id}`);
    } catch (error) {
      console.error('Delete email config error:', error);
      throw error;
    }
  }

  async getActiveEmailConfig(): Promise<{ success: boolean; data: EmailConfig }> {
    try {
      const response = await api.get('/admin/parametrizacoes/email-config-active');
      return response.data;
    } catch (error) {
      console.error('Get active email config error:', error);
      throw error;
    }
  }

  async testEmailConfig(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/admin/parametrizacoes/email-config/${id}/test`);
      return response.data;
    } catch (error) {
      console.error('Test email config error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Email Signatures
  // ============================================
  async getEmailSignatures(): Promise<SettingsResponse<EmailSignature>> {
    try {
      const response = await api.get('/admin/parametrizacoes/email-signatures');
      return response.data;
    } catch (error) {
      console.error('Get email signatures error:', error);
      throw error;
    }
  }

  async getEmailSignature(id: string): Promise<{ success: boolean; data: EmailSignature }> {
    try {
      const response = await api.get(`/admin/parametrizacoes/email-signatures/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get email signature error:', error);
      throw error;
    }
  }

  async createEmailSignature(data: EmailSignatureInput): Promise<EmailSignature> {
    try {
      const response = await api.post('/admin/parametrizacoes/email-signatures', data);
      return response.data.data;
    } catch (error) {
      console.error('Create email signature error:', error);
      throw error;
    }
  }

  async updateEmailSignature(id: string, data: Partial<EmailSignatureInput>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/email-signatures/${id}`, data);
    } catch (error) {
      console.error('Update email signature error:', error);
      throw error;
    }
  }

  async deleteEmailSignature(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/email-signatures/${id}`);
    } catch (error) {
      console.error('Delete email signature error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Email Templates
  // ============================================
  async getEmailTemplates(): Promise<SettingsResponse<EmailTemplate>> {
    try {
      const response = await api.get('/admin/parametrizacoes/email-templates');
      return response.data;
    } catch (error) {
      console.error('Get email templates error:', error);
      throw error;
    }
  }

  async getEmailTemplate(id: string): Promise<{ success: boolean; data: EmailTemplate }> {
    try {
      const response = await api.get(`/admin/parametrizacoes/email-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get email template error:', error);
      throw error;
    }
  }

  async createEmailTemplate(data: EmailTemplateInput): Promise<EmailTemplate> {
    try {
      const response = await api.post('/admin/parametrizacoes/email-templates', data);
      return response.data.data;
    } catch (error) {
      console.error('Create email template error:', error);
      throw error;
    }
  }

  async updateEmailTemplate(id: string, data: Partial<EmailTemplateInput>): Promise<void> {
    try {
      await api.put(`/admin/parametrizacoes/email-templates/${id}`, data);
    } catch (error) {
      console.error('Update email template error:', error);
      throw error;
    }
  }

  async deleteEmailTemplate(id: string): Promise<void> {
    try {
      await api.delete(`/admin/parametrizacoes/email-templates/${id}`);
    } catch (error) {
      console.error('Delete email template error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Email Logs
  // ============================================
  async getEmailLogs(params?: EmailLogsParams): Promise<EmailLogsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.email) queryParams.append('email', params.email);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const url = queryParams.toString()
        ? `/admin/parametrizacoes/email-logs?${queryParams}`
        : '/admin/parametrizacoes/email-logs';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Get email logs error:', error);
      throw error;
    }
  }

  async getEmailLog(id: string): Promise<{ success: boolean; data: EmailLog }> {
    try {
      const response = await api.get(`/admin/parametrizacoes/email-logs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get email log error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Email Stats
  // ============================================
  async getEmailStats(): Promise<{ success: boolean; data: EmailStats }> {
    try {
      const response = await api.get('/admin/parametrizacoes/email-stats');
      return response.data;
    } catch (error) {
      console.error('Get email stats error:', error);
      throw error;
    }
  }

  // ============================================
  // PARAMETRIZACOES - Send Email
  // ============================================
  async sendEmail(data: SendEmailInput): Promise<SendEmailResponse> {
    try {
      const response = await api.post('/admin/parametrizacoes/email-send', data);
      return response.data;
    } catch (error) {
      console.error('Send email error:', error);
      throw error;
    }
  }

  async sendEmailTemplate(data: SendEmailTemplateInput): Promise<SendEmailResponse> {
    try {
      const response = await api.post('/admin/parametrizacoes/email-send-template', data);
      return response.data;
    } catch (error) {
      console.error('Send email template error:', error);
      throw error;
    }
  }
  // ============================================
  // Card Types
  // ============================================

  async getCardTypes(): Promise<SettingsResponse<CardType>> {
    try {
      const response = await api.get('/admin/settings/card-types?all=true');
      return response.data;
    } catch (error) {
      console.error('Get card types error:', error);
      throw error;
    }
  }

  async createCardType(data: Partial<CardType>): Promise<{ id: string }> {
    try {
      const response = await api.post('/admin/settings/card-types', data);
      return response.data.data;
    } catch (error) {
      console.error('Create card type error:', error);
      throw error;
    }
  }

  async updateCardType(id: string, data: Partial<CardType>): Promise<void> {
    try {
      await api.put(`/admin/settings/card-types/${id}`, data);
    } catch (error) {
      console.error('Update card type error:', error);
      throw error;
    }
  }

  async deleteCardType(id: string): Promise<void> {
    try {
      await api.delete(`/admin/settings/card-types/${id}`);
    } catch (error) {
      console.error('Delete card type error:', error);
      throw error;
    }
  }

  async getEntityCardTypes(entityId: string): Promise<SettingsResponse<CardType>> {
    try {
      const response = await api.get(`/admin/settings/entities/${entityId}/card-types`);
      return response.data;
    } catch (error) {
      console.error('Get entity card types error:', error);
      throw error;
    }
  }

  async updateEntityCardTypes(entityId: string, cardTypeIds: string[]): Promise<void> {
    try {
      await api.put(`/admin/settings/entities/${entityId}/card-types`, { cardTypeIds });
    } catch (error) {
      console.error('Update entity card types error:', error);
      throw error;
    }
  }

  // ── CORS Origins ────────────────────────────────────────────────────────────

  async getCorsOrigins(): Promise<{ success: boolean; data: CorsOrigin[] }> {
    const response = await api.get('/admin/settings/cors-origins');
    return response.data;
  }

  async addCorsOrigin(origin: string, descricao?: string): Promise<{ success: boolean; data: CorsOrigin }> {
    const response = await api.post('/admin/settings/cors-origins', { origin, descricao });
    return response.data;
  }

  async toggleCorsOrigin(id: number): Promise<{ success: boolean; data: CorsOrigin }> {
    const response = await api.patch(`/admin/settings/cors-origins/${id}/toggle`);
    return response.data;
  }

  async deleteCorsOrigin(id: number): Promise<void> {
    await api.delete(`/admin/settings/cors-origins/${id}`);
  }
}

export interface CorsOrigin {
  id: number | null;
  origin: string;
  descricao: string | null;
  ativo: number;
  criado_por: string | null;
  created_at: string | null;
  fonte: 'sistema' | 'env' | 'bd';
}

export default new SettingsService();
